import React, { FunctionComponent as FC, useState, useRef, useEffect } from 'react';
import _ from 'lodash';
import './index.scss';

import SideViewTooltipComponent from '../../SideViewTooltipComponent';
import { TooltipTypes } from '../../../../../types/data';
import { PIViewTypes } from '../../../../../types/data';

import { schemeCategory10 } from 'd3-scale-chromatic';
import { scaleOrdinal } from 'd3-scale';
import { prepareData } from '../RadarChart/prepareData';
import { createRadarChart } from '../RadarChart/createRadarChart';


interface DonutAndRadarChartProps {
  // DonutChart
  donutChartDataOuter: PIViewTypes.DonutChartDataUnit[];
  donutChartDataInner: PIViewTypes.DonutChartDataUnit[];
  donutChartDiameter: number;
  // diameter={viewParam.width * 0.35}
  donutChartColorMap: Record<string, string>;

  // RadarChart
  radarChartTitle: string;
  radarChartData: PIViewTypes.RadarChartEntity[];
  radarChartDiameter: number;
  // diameter={viewParam.width * 0.24}
  radarChartColorMap: Record<string, string>;
}

const DonutAndRadarChart: React.FC<DonutAndRadarChartProps> = (props) => {

  const { donutChartDataOuter, donutChartDataInner, donutChartDiameter, donutChartColorMap, radarChartTitle, radarChartData, radarChartDiameter, radarChartColorMap } = props;

  // 定义Tooltip的状态
  const [tooltipOpacity, setTooltipOpacity] = useState(0);
  const [tooltipPosition, setTooltipPosition] = useState({ left: 0, top: 0 });
  const [tooltipData, setTooltipData] = useState<TooltipTypes.TooltipDataUnit[]>([{ " ": 0 }])

  // 处理鼠标进入事件
  const handleMouseEnter = (event: React.MouseEvent<SVGPathElement>, data: PIViewTypes.DonutChartDataUnit) => {
    const attributeName = Object.keys(data)[0];
    const value = data[attributeName];
    setTooltipData([{ [attributeName]: value }]);
    setTooltipPosition({
      left: event.clientX + 24,
      top: event.clientY + 24,
    });
    setTooltipOpacity(1);
  };

  // 处理鼠标离开事件
  const handleMouseLeave = () => {
    setTooltipOpacity(0);
  };

  // 描述donutn chart片段的SVG路径的辅助函数。
  const describeArc = (
    x: number, // 圆心的x坐标
    y: number, // 圆心的y坐标
    radius: number, // 外圆半径
    startAngle: number, // 起始角度
    endAngle: number, // 结束角度
    innerRadius: number = 0 // 内圆半径，默认为0
  ) => {
    const start = polarToCartesian(x, y, radius, endAngle); // 外圆弧的起点
    const end = polarToCartesian(x, y, radius, startAngle); // 外圆弧的终点
    const innerStart = polarToCartesian(x, y, innerRadius, endAngle); // 内圆弧的起点
    const innerEnd = polarToCartesian(x, y, innerRadius, startAngle); // 内圆弧的终点
    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1'; // 大圆弧标志
    // 组合成完整的SVG路径字符串
    const d = [
      'M', start.x, start.y, // 移动到外圆弧起点
      'A', radius, radius, 0, largeArcFlag, 0, end.x, end.y, // 绘制外圆弧
      'L', innerEnd.x, innerEnd.y, // 从外圆弧终点绘制线到内圆弧的终点
      'A', innerRadius, innerRadius, 0, largeArcFlag, 1, innerStart.x, innerStart.y, // 绘制内圆弧，反方向
      'Z' // 封闭路径
    ].join(' ');
    return d;
  };

  // 从极坐标转换为笛卡尔坐标的辅助函数。
  const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0; // 将角度转换为弧度，并修正起点在正上方
    return {
      x: centerX + radius * Math.cos(angleInRadians), // 计算x坐标
      y: centerY + radius * Math.sin(angleInRadians) // 计算y坐标
    };
  };

  // 累计数据数组中的值的辅助函数。
  const sumValues = (data: PIViewTypes.DonutChartDataUnit[]) => data.reduce((sum, item) => sum + Object.values(item)[0], 0);
  const totalOuter = sumValues(donutChartDataOuter); // 外层数据的总和
  const totalInner = sumValues(donutChartDataInner); // 内层数据的总和

  // 计算每个片段的起始和结束角度的辅助函数。
  const calculateAngles = (data: PIViewTypes.DonutChartDataUnit[], total: number) => {
    let startAngle = 0;
    return data.map((item) => {
      const value = Object.values(item)[0];
      const endAngle = startAngle + (value / total) * 360; // 根据比例计算结束角度
      const angles = { startAngle, endAngle };
      startAngle = endAngle; // 下一个片段的起始角度是当前结束角度
      return angles;
    });
  };

  const outerAngles = calculateAngles(donutChartDataOuter, totalOuter); // 计算外层数据的角度
  const innerAngles = calculateAngles(donutChartDataInner, totalInner); // 计算内层数据的角度

  // 计算外层和内层donut的半径。
  const outerRadiusOuterDonut = donutChartDiameter / 2; // 外层donut的外半径
  const outerRadiusInnerDonut = outerRadiusOuterDonut * 0.94; // 外层donut的内半径
  const innerRadiusOuterDonut = outerRadiusOuterDonut * 0.92; // 外层donut的内半径
  const innerRadiusInnerDonut = outerRadiusOuterDonut * 0.82; // 内层donut的内半径


  //////////////// RadarChart ////////////////
  const radarChartRef = useRef(null);
  const donutChartRef = useRef(null);
  var radarChartIdString = `${radarChartTitle}-component`;

  useEffect(() => {
    // console.log("==> radarChartDiameter", radarChartDiameter);
    // 准备配置选项
    const cfg = {
        w: radarChartDiameter, // 使用传入的直径
        h: radarChartDiameter,
        margin: {top: 0, right: 0, bottom: 0, left: 0}, // 设置为0以确保与甜甜圈图圆心对齐
        color: radarChartColorMap || scaleOrdinal(schemeCategory10)

    };
    // 准备数据格式
    var formattedData = prepareData(radarChartData);
    // 调用 RadarChart 函数来创建图表
    createRadarChart(`.${radarChartIdString}`, formattedData, cfg);
  }, [radarChartData, radarChartDiameter, radarChartColorMap]);



  return (
    <>
      <div className='donut-and-radar-chart-component'>
        {/* 渲染Tooltip */}
        <SideViewTooltipComponent
            tooltipPosition={tooltipPosition}
            tooltipOpacity={tooltipOpacity}
            tooltipData={tooltipData}
          />

        {/* RadarChart */}
        <div className={`radar-chart-component ${radarChartIdString}`}
             style={{ width: donutChartDiameter, height: donutChartDiameter }}>
          <svg ref={radarChartRef}></svg>
        </div>

        {/* Donut Chart */}
        <div className='donut-chart-component'
             style={{ width: donutChartDiameter, height: donutChartDiameter }}>
          <svg
            width={donutChartDiameter}
            height={donutChartDiameter}
            viewBox={`0 0 ${donutChartDiameter} ${donutChartDiameter}`}
            ref={donutChartRef}
          >
            <g>
              {outerAngles.map((angles, index) => {
                const key = Object.keys(donutChartDataOuter[index])[0];
                return (
                  <path
                    key={key}
                    d={describeArc(
                      donutChartDiameter / 2,
                      donutChartDiameter / 2,
                      outerRadiusOuterDonut,
                      angles.startAngle,
                      angles.endAngle,
                      outerRadiusInnerDonut
                    )}
                    fill={donutChartColorMap[key]|| "black"} // 设置片段颜色
                    fillOpacity={0.3}
                    onMouseEnter={(e) => handleMouseEnter(e, donutChartDataOuter[index])}
                    onMouseLeave={handleMouseLeave}
                  />
                );
              })}
              {innerAngles.map((angles, index) => {
                const key = Object.keys(donutChartDataInner[index])[0];
                return (
                  <path
                    key={key}
                    d={describeArc(
                      donutChartDiameter / 2,
                      donutChartDiameter / 2,
                      innerRadiusOuterDonut,
                      angles.startAngle,
                      angles.endAngle,
                      innerRadiusInnerDonut
                    )}
                    fill={donutChartColorMap[key] || "black"} // 设置片段颜色
                    fillOpacity={0.6}
                    onMouseEnter={(e) => handleMouseEnter(e, donutChartDataInner[index])}
                    onMouseLeave={handleMouseLeave}
                  />
                );
              })}
            </g>
          </svg>
        </div>
      </div>
    </>
  );
};

export default DonutAndRadarChart;


